import React, { useState, useEffect, useCallback } from 'react';
import {
  Share2, Copy, CheckCheck, Loader2, Eye, MousePointerClick, Plus, Clock,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import logger from '../lib/logger';

const COMMON_LABELS = ['CFO', 'CEO', 'Board', 'HR Lead', 'Division Head'];

/**
 * Reusable dialog for minting public, read-only share links to an
 * Executive Report Card. Supports re-share for multi-stakeholder
 * distribution: each link can carry a recipient label so the admin
 * sees per-recipient view/CTA counts ("CFO: 8 views", "Board: 3").
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (boolean) => void
 *   report: { case_id, skill_name }
 */
export default function ShareReportCardDialog({ open, onOpenChange, report }) {
  const [ttlHours, setTtlHours] = useState(24 * 30);
  const [recipientLabel, setRecipientLabel] = useState('');
  const [generating, setGenerating] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareExpiresAt, setShareExpiresAt] = useState(null);
  const [shareLabel, setShareLabel] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pastLinks, setPastLinks] = useState([]);
  const [loadingPast, setLoadingPast] = useState(false);

  const resetCreateState = useCallback(() => {
    setShareUrl(null); setShareExpiresAt(null); setShareLabel(null);
    setCopied(false); setRecipientLabel(''); setTtlHours(24 * 30);
  }, []);

  const fetchPastLinks = useCallback(async () => {
    if (!report?.case_id) return;
    setLoadingPast(true);
    try {
      const { data } = await api.get(
        `/people-engine/report-card/${report.case_id}/share-status`,
      );
      setPastLinks(data.tokens || []);
    } catch (err) {
      logger.error('share-status-fetch-failed', err);
    } finally {
      setLoadingPast(false);
    }
  }, [report?.case_id]);

  useEffect(() => {
    if (open) {
      fetchPastLinks();
    } else {
      resetCreateState();
      setPastLinks([]);
    }
  }, [open, fetchPastLinks, resetCreateState]);

  const handleGenerate = async () => {
    if (!report?.case_id) return;
    setGenerating(true);
    try {
      const payload = { ttl_hours: ttlHours };
      const labelTrimmed = recipientLabel.trim().slice(0, 60);
      if (labelTrimmed) payload.recipient_label = labelTrimmed;
      const { data } = await api.post(
        `/people-engine/report-card/${report.case_id}/share`,
        payload,
      );
      const fullUrl = data.url?.startsWith('http')
        ? data.url
        : `${window.location.origin}${data.url}`;
      setShareUrl(fullUrl);
      setShareExpiresAt(data.expires_at);
      setShareLabel(data.recipient_label);
      if (data.deduped) {
        toast.info(
          labelTrimmed
            ? `Reusing recent link for ${labelTrimmed}`
            : 'Reusing your recent share link',
          { description: 'A link for this combination was created in the last 5 minutes.' },
        );
      } else {
        toast.success(
          labelTrimmed ? `Link created for ${labelTrimmed}` : 'Share link created',
        );
      }
      // Refresh past list so the new entry shows up if user clicks Generate another.
      fetchPastLinks();
    } catch (err) {
      logger.error('share-link-create-failed', err);
      toast.error(err?.response?.data?.detail || 'Could not create share link');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (urlToCopy = shareUrl) => {
    if (!urlToCopy) return;
    try {
      await navigator.clipboard.writeText(urlToCopy);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      toast.error('Could not copy — please copy manually');
    }
  };

  const handleGenerateAnother = () => {
    resetCreateState();
  };

  const totalViews = pastLinks.reduce((s, t) => s + (t.view_count || 0), 0);
  const hasReshareSignal = totalViews > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="share-report-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-indigo-500" />
            {hasReshareSignal && !shareUrl ? 'Share with another stakeholder' : 'Share Report Card'}
          </DialogTitle>
          <DialogDescription>
            {hasReshareSignal && !shareUrl
              ? <>Your share of <strong>{report?.skill_name || 'this report'}</strong> already has <strong>{totalViews} view{totalViews === 1 ? '' : 's'}</strong>. Generate a fresh link for someone else and track them separately.</>
              : <>Anyone with the link can view a read-only snapshot of <strong>{report?.skill_name || 'this report'}</strong>. No login required.</>}
          </DialogDescription>
        </DialogHeader>

        {!shareUrl ? (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5 block">
                Who's this for? <span className="text-slate-400 font-normal normal-case lowercase">(optional)</span>
              </label>
              <input
                type="text"
                maxLength={60}
                value={recipientLabel}
                onChange={(e) => setRecipientLabel(e.target.value)}
                placeholder="e.g. CFO Smith, Q2 Board, Sales VP"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                data-testid="share-recipient-input"
                disabled={generating}
              />
              <div className="mt-1.5 flex flex-wrap gap-1">
                {COMMON_LABELS.map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setRecipientLabel(l)}
                    className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 hover:bg-indigo-50 hover:text-indigo-700 text-slate-600 transition-colors"
                    data-testid={`share-quick-label-${l.replace(/\s/g, '-').toLowerCase()}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5 block">
                Link expires in
              </label>
              <select
                value={ttlHours}
                onChange={(e) => setTtlHours(Number(e.target.value))}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                data-testid="share-ttl-select"
                disabled={generating}
              >
                <option value={24 * 7}>7 days (recommended for re-share)</option>
                <option value={24 * 30}>30 days</option>
                <option value={24 * 60}>60 days</option>
                <option value={24 * 90}>90 days (max)</option>
              </select>
            </div>
            <p className="text-xs text-slate-500">
              The recipient sees only the executive summary, before/after numbers,
              and the AI-generated narrative. No personal data, employee records, or
              underlying program data is exposed.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {shareLabel && (
              <p className="text-xs text-slate-500">
                Tracking views for <strong className="text-slate-700">{shareLabel}</strong>
              </p>
            )}
            <div className="flex items-stretch gap-2">
              <input
                value={shareUrl}
                readOnly
                onFocus={(e) => e.target.select()}
                className="flex-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 font-mono"
                data-testid="share-url-input"
              />
              <button
                type="button"
                onClick={() => handleCopy()}
                className="inline-flex items-center justify-center px-3 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                data-testid="share-copy-btn"
                aria-label="Copy link"
              >
                {copied ? <CheckCheck className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
            {shareExpiresAt && (
              <p className="text-xs text-slate-500">
                Expires {new Date(shareExpiresAt).toLocaleString(undefined,
                  { dateStyle: 'medium', timeStyle: 'short' })}
              </p>
            )}
          </div>
        )}

        {/* Past links — always shown when there's at least one */}
        {pastLinks.length > 0 && (
          <div className="border-t border-slate-100 pt-3 -mx-1" data-testid="share-past-links">
            <div className="flex items-center justify-between px-1 mb-1.5">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Active links · {pastLinks.length}
              </p>
              {loadingPast && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
            </div>
            <div className="max-h-44 overflow-y-auto px-1 space-y-1">
              {pastLinks.map((t) => (
                <div
                  key={t.token}
                  className="flex items-center gap-2 text-xs px-2 py-1.5 rounded-md hover:bg-slate-50 group"
                  data-testid={`share-past-link-${t.token.slice(0, 8)}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-slate-800 truncate">
                        {t.recipient_label || <em className="text-slate-400">unlabelled</em>}
                      </span>
                      {t.expired && (
                        <span className="text-[9px] uppercase font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded-full">expired</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-slate-500 mt-0.5">
                      <span className="inline-flex items-center gap-0.5">
                        <Eye className="h-3 w-3" /> {t.view_count || 0}
                      </span>
                      {t.cta_click_count > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-emerald-700">
                          <MousePointerClick className="h-3 w-3" /> {t.cta_click_count}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-0.5 text-slate-400">
                        <Clock className="h-3 w-3" />
                        {t.expires_at
                          ? new Date(t.expires_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                          : '—'}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(t.url)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-slate-500 hover:text-indigo-600 hover:bg-indigo-50"
                    title={`Copy ${t.recipient_label || 'link'}`}
                    aria-label="Copy link"
                    data-testid={`share-past-copy-${t.token.slice(0, 8)}`}
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="sm:justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="share-close-btn">
            {shareUrl ? 'Done' : 'Cancel'}
          </Button>
          {shareUrl ? (
            <Button
              onClick={handleGenerateAnother}
              variant="outline"
              data-testid="share-generate-another-btn"
              className="text-indigo-700 border-indigo-200 hover:bg-indigo-50"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Generate another
            </Button>
          ) : (
            <Button
              onClick={handleGenerate}
              disabled={generating}
              data-testid="share-generate-btn"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {generating ? (
                <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Generating…</>
              ) : (
                <><Share2 className="h-4 w-4 mr-1.5" /> Create link</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
